import { inject, Injectable } from '@angular/core';
import { ITeaser } from '../../models/teaser/teaser';
import { DateService } from '../date/date.service';

/** Teaser key that flags an article (or article key/variant) as "new" via the OCS NewArticle checkbox. */
export const NEW_TEASER_KEY = 'new';

@Injectable({
  providedIn: 'root'
})
export class TeaserService {
  // Services
  private readonly dateService = inject(DateService);

  // Methods
  /**
   * Checks whether any of the given teasers carries the OCS "new" key.
   * Plain label strings are ignored since only `ITeaser` objects carry a key.
   * @param teasers Teasers to inspect.
   * @returns true if at least one teaser has `key === 'new'`.
   */
  public hasNewKey(...teasers: (ITeaser | string | null | undefined)[]): boolean {
    return teasers.some((teaser) => typeof teaser === 'object' && teaser?.key === NEW_TEASER_KEY);
  }

  /**
   * Determines whether an article should be flagged as "new".
   * An article counts as new when the OCS "new" teaser key is set (manual flag, key === 'new') OR
   * the creation date lies within the configured "new for days" window (Designer setting).
   * @param createdOn Creation date of the article or article key/variant.
   * @param newForDays Number of days an article is considered new (Designer setting); 0 turns the
   * flag off completely, defaults to 10.
   * @param teasers Teasers that may carry the OCS "new" key.
   * @returns true if the article is considered new.
   */
  public isNewArticle(
    createdOn: Date | string | undefined,
    newForDays: number | undefined,
    ...teasers: (ITeaser | string | null | undefined)[]
  ): boolean {
    const days = newForDays ?? 10;
    // 0 days is the "never flag as new" switch and also overrules the manually set OCS key
    if (days <= 0) return false;

    return this.hasNewKey(...teasers) || this.dateService.isWithinLastDays(createdOn, days);
  }

  /**
   * Filters out the OCS "new" teaser when the new flag is switched off (0 days).
   * The teaser carries its own name and color, so it has to be dropped from the teaser badge as
   * well - otherwise a colored "Neu" badge stays visible after `isNewArticle()` already hid the flag.
   * @param teaser Teaser that should be rendered.
   * @param newForDays Number of days an article is considered new (Designer setting); 0 hides the
   * "new" teaser, defaults to 10.
   * @returns The teaser, or null when it is the "new" teaser and the flag is switched off.
   */
  public visibleTeaser(teaser: ITeaser | null | undefined, newForDays: number | undefined): ITeaser | null {
    if ((newForDays ?? 10) <= 0 && this.hasNewKey(teaser)) return null;

    return teaser ?? null;
  }
}
